import React, {useCallback} from 'react';
import {Formik} from 'formik';
import {useTranslation} from 'react-i18next';
import {
    MakePaymentDialogProps,
    MakePaymentFormType,
    newPaymentValidationSchemaHelper,
    NO_VALIDATION_METHODS,
    useStyles,
    validateBankAccount,
    validateCreditCard,
    validateDirectDebitNL,
    validatePayNearMe,
} from './MakePaymentDialog.utils';
import {
    convertCardValidatorTypeToPaymentType,
    convertPaymentMethodToPaymentType,
    getCardType,
    paymentMethodTypeNameFunc
} from '../PaymentMethodForm/PaymentMethodForm.utils';
import DialogContainer, {DialogButton,} from '../../../AlertDialog/DialogContainer';
import PaymentMethodForm from '../PaymentMethodForm/PaymentMethodForm';
import {PaymentMethodType} from '../../../../store/types/Billing';
import {useDispatch} from 'react-redux';
import {RegisterTransaction,} from '../../../../store/types/Payment';
import {actions} from '../../../../store';
import Loader from '../../../Loader/Loader';
import PaymentAmountForm from '../PaymentAmountForm/PaymentAmountForm';
import PaymentMethodDetails from '../../../Payments/PaymentMethodDetails/PaymentMethodDetails';
import CreationButton from '../../../CreationButton/CreationButton';
import CustomizedTextField from '../../../TextField/TextField';
import IconWithTooltip from '../../../Tooltip/IconWithTooltip';
import CustomizedCheckbox from '../../../Checkbox/Checkbox';
import _ from 'lodash';
import CustomizedTooltip from '../../../Tooltip/Tooltip';
import {YesNo} from '../../../../store/types/CallScreening';
import classNames from 'classnames';
import {useMakePaymentData} from "../../../../hooks/useMakePaymentData";

const MakePaymentDialog: React.VFC<MakePaymentDialogProps> = (
    {
        isOpen,
        toggleVisibility,
    }) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const {initFormData, data} = useMakePaymentData()
    const {
        paymentMethod,
        customerInfo,
        paymentsMethods,
        ownerPaymentsMethods,
        isMakeTransactionLoading,
        currency
    } = data


    const getCurrentOwnerPaymentMethod = (
        type: PaymentMethodType,
        number: string,
    ) => {
        if (type === PaymentMethodType.CreditCard) {
            const validatorCardType = getCardType(number);
            if (validatorCardType) {
                const cardType = convertCardValidatorTypeToPaymentType(
                    validatorCardType
                );

                return ownerPaymentsMethods?.find(
                    (v) => v.payment_method === cardType,
                );
            }
        } else {
            return ownerPaymentsMethods?.find(
                (v) => convertPaymentMethodToPaymentType(v.payment_method) === type
            );
        }
        return undefined;
    };

    const isSelectedMethodBlockedAsSaveDefault = (
        type: PaymentMethodType,
        number: string,
    ) => {
        const pm = getCurrentOwnerPaymentMethod(type, number);
        if (!pm) return false;

        return (
            (pm.remote_cc_storage === YesNo.Yes && !customerInfo?.email) || pm.ext_auth === YesNo.Yes
        );
    };

    const isSelectedMethodRequiresExternalAuth = (
        type: PaymentMethodType,
        number: string
    ) => {
        const pm = getCurrentOwnerPaymentMethod(type, number);
        if (!pm) return false;

        return pm.ext_auth === YesNo.Yes;
    }

    const validators: Partial<Record<PaymentMethodType, (v: MakePaymentFormType) => boolean>> = {
        [PaymentMethodType.CreditCard]: validateCreditCard,
        [PaymentMethodType.BankAccount]: validateBankAccount,
        [PaymentMethodType.DirectDebitNL]: validateDirectDebitNL,
        [PaymentMethodType.PayNearMe]: validatePayNearMe,
    };

    const isPaymentMethodAvailableToSave = (values: MakePaymentFormType) => {
        if (values.isDefaultPaymentMethod) {
            return isDefaultPaymentMethodValid(values);
        }

        if (isSelectedMethodRequiresExternalAuth(values.paymentType, values.cardNumber)) {
            return true;
        }

        if (NO_VALIDATION_METHODS.includes(values.paymentType)) {
            return true;
        }

        const validator = validators[values.paymentType as keyof typeof validators];
        return validator ? validator(values) : true;
    };
    const isDefaultPaymentMethodValid = (values: MakePaymentFormType) =>
        paymentMethod?.csc_required === 1 ? !!values.cvv : true;


    const onSubmit = useCallback((form: MakePaymentFormType) => {
        const pm = getCurrentOwnerPaymentMethod(form.paymentType, form.cardNumber);
        if (pm && pm.ext_auth === YesNo.Yes) {
            const object: RegisterTransaction = {
                amount: +form.amount,
                i_payment_method: pm.i_payment_method,
                owner_type: 'Customer',
                i_owner: customerInfo?.i_customer,
                i_online_payment_processor: pm.i_online_payment_processor,
                callback: () => {
                    actions.makePayment.request(form);
                }
            };
            dispatch(actions.registerTransaction.request(object));
        } else {
            dispatch(actions.makePayment.request(form));
        }
    }, [customerInfo]);

    return (
        <Formik<MakePaymentFormType>
            initialValues={initFormData}
            validate={(values) => {
                const schema = newPaymentValidationSchemaHelper(
                    values.isDefaultPaymentMethod,
                    ownerPaymentsMethods || [],
                    paymentMethod,
                    currency?.iso_4217,
                );

                try {
                    schema.validateSync(values, {
                        abortEarly: false,
                        context: values,
                    });
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } catch (error: any) {
                    return error.inner.reduce(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (errors: any, currentError: any) => {
                            errors = _.set(
                                errors,
                                currentError.path,
                                currentError.message,
                            );
                            return errors;
                        },
                        {},
                    );
                }

                return {};
            }}
            onSubmit={onSubmit}
            enableReinitialize
            validateOnChange={false}
        >
            {({
                  submitForm,
                  resetForm,
                  values,
                  setFieldError,
                  setFieldValue,
                  handleChange,
                  errors,
                  setErrors,
              }) => {
                const disabledSaveDefault = isSelectedMethodBlockedAsSaveDefault(
                    values.paymentType,
                    values.cardNumber,
                );

                return (
                    <DialogContainer
                        isOpen={isOpen}
                        dataQa="make-payment-dialog"
                        header={t('screens:billing.makeAPayment')}
                        className={classes.modalContainer}
                        disableEnforceFocus
                        dialogActionsButtons={[
                            <DialogButton
                                key="cancel"
                                label={t('common:cancel')}
                                onClick={() => {
                                    toggleVisibility?.();
                                    setTimeout(() => {
                                        resetForm();
                                    }, 300);
                                }}
                            />,
                            <DialogButton
                                key="save"
                                label={
                                    isSelectedMethodRequiresExternalAuth(
                                        values.paymentType,
                                        values.cardNumber)
                                        ? t('screens:billing.proceedToPaymentMethod', {
                                            payment_method: paymentMethodTypeNameFunc(values.paymentType, t)
                                        })
                                        : t('screens:billing.pay')
                                }
                                onClick={submitForm}
                                disabled={
                                    !isPaymentMethodAvailableToSave(
                                        values,
                                    )
                                    ||
                                    //todo! implement the logic
                                    isSelectedMethodRequiresExternalAuth(
                                        values.paymentType,
                                        values.cardNumber)
                                }
                                primary
                            />,
                        ]}
                    >
                        <PaymentAmountForm/>

                        {(values.isDefaultPaymentMethod ||
                            paymentsMethods.length > 1) && (
                            <div className={classes.sectionHeader}>
                                {t('screens:billing.payWith')}
                            </div>
                        )}

                        {paymentMethod && values.isDefaultPaymentMethod ? (
                            <div className={classes.defaultPaymentContainer}>
                                <div
                                    className={
                                        classes.defaultPaymentInsideContainer
                                    }
                                >
                                    <PaymentMethodDetails
                                        method={paymentMethod}
                                    />

                                    {paymentMethod.csc_required === 1 && (
                                        <CustomizedTextField
                                            id="cvv"
                                            label={t('screens:billing.cvv')}
                                            value={values.cvv}
                                            helperText={errors.cvv}
                                            setFieldError={setFieldError}
                                            onChange={handleChange}
                                            required
                                            iconPosition="end"
                                            icon={
                                                <IconWithTooltip
                                                    dataQa="cvv-tooltip"
                                                    tooltipText={t(
                                                        'tooltips:billing.cvv',
                                                    )}
                                                />
                                            }
                                            maxLength={4}
                                            className={classes.cvvTextfield}
                                        />
                                    )}
                                </div>
                                <CreationButton
                                    title={t('screens:billing.useOtherMethod')}
                                    className={classes.otherMethodButton}
                                    onClick={() => {
                                        setErrors({});
                                        setFieldValue(
                                            'isDefaultPaymentMethod',
                                            false,
                                        );
                                    }}
                                    withPlus={false}
                                />
                            </div>
                        ) : (
                            <div className={classes.payForm}>
                                <div>
                                    <PaymentMethodForm
                                        paymentMethodsTypes={paymentsMethods}
                                    />
                                </div>

                                <CustomizedTooltip
                                    title={t(
                                        'tooltips:billing.saveAsDefaultPaymentMethodDisabled',
                                    )}
                                    disableHoverListener={!disabledSaveDefault ||
                                        isSelectedMethodRequiresExternalAuth(
                                            values.paymentType,
                                            values.cardNumber)}
                                    copy={false}
                                >
                                    <div className={classNames(classes.checkboxContainer,
                                        values.paymentType !== PaymentMethodType.BankAccount &&
                                        values.paymentType !== PaymentMethodType.CreditCard &&
                                        values.paymentType !== PaymentMethodType.DirectDebitNL &&
                                        values.paymentType !== PaymentMethodType.PayNearMe &&
                                        values.paymentType !== PaymentMethodType.PayPal &&
                                        classes.checkboxContainerMargin
                                    )}>
                                        <CustomizedCheckbox
                                            dataQa="set-as-default-method"
                                            label={t(
                                                'screens:billing.saveAsDefaultPaymentMethod',
                                            )}
                                            checked={
                                                values.setAsDefaultPaymentMethod
                                            }
                                            onChange={() =>
                                                setFieldValue(
                                                    'setAsDefaultPaymentMethod',
                                                    !values.setAsDefaultPaymentMethod,
                                                )
                                            }
                                            disabled={disabledSaveDefault}
                                        />
                                        <IconWithTooltip
                                            tooltipText={t(
                                                'tooltips:billing.saveAsDefaultPaymentMethod',
                                            )}
                                        />
                                    </div>
                                </CustomizedTooltip>

                                {paymentMethod && (
                                    <CreationButton
                                        title={t(
                                            'screens:billing.useDefaultMethod',
                                        )}
                                        className={classes.defaultMethodButton}
                                        onClick={() => {
                                            setErrors({});
                                            setFieldValue(
                                                'isDefaultPaymentMethod',
                                                true,
                                            );
                                        }}
                                        withPlus={false}
                                    />
                                )}
                            </div>
                        )}

                        {isMakeTransactionLoading && (
                            <Loader
                                dataQa="edit-payment-method-loader"
                                absolutePosition
                            />
                        )}
                    </DialogContainer>
                );
            }}
        </Formik>
    );
};

export default MakePaymentDialog;
